import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useNavigate } from 'react-router-dom';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Deliveries from './pages/Deliveries';
import DeletedDeliveries from './pages/DeletedDeliveries';
import Customers from './pages/Customers';
import PullOuts from './pages/PullOuts';
import Printing from './pages/Printing';
import PostedSalesInvoice from './pages/PostedSalesInvoice';
import Settings from './pages/Settings';
import ARReceived from './pages/ARReceived';
import { formatRole } from './roles';
import { Truck, LayoutDashboard, Users, Package, Printer, Menu, X, FileText, Archive, Settings as SettingsIcon, Receipt, LogOut } from 'lucide-react';
import './App.css';

// Role-based menu configuration
const roleMenus = {
  admin: [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/deliveries', label: 'Deliveries', icon: Truck },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/pull-outs', label: 'Pull-Outs', icon: Package },
    { path: '/printing', label: 'For Printing', icon: Printer },
    { path: '/posted-sales-invoice', label: 'Posted Sales Invoice', icon: FileText },
    { path: '/ar-received', label: 'AR Received', icon: Receipt },
    { path: '/deleted-deliveries', label: 'Deleted Deliveries', icon: Archive },
    { path: '/settings', label: 'Settings', icon: SettingsIcon },
  ],
  account_supervisor: [
    { path: '/ar-received', label: 'AR Received', icon: Receipt },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/posted-sales-invoice', label: 'Posted Sales Invoice', icon: FileText },
    { path: '/settings', label: 'Settings', icon: SettingsIcon },
  ],
  account_receivable: [
    { path: '/ar-received', label: 'AR Received', icon: Receipt },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/posted-sales-invoice', label: 'Posted Sales Invoice', icon: FileText },
  ],
  dispatch: [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/deliveries', label: 'Deliveries', icon: Truck },
    { path: '/customers', label: 'Customers', icon: Users },
    { path: '/pull-outs', label: 'Pull-Outs', icon: Package },
    { path: '/printing', label: 'For Printing', icon: Printer },
    { path: '/posted-sales-invoice', label: 'Posted Sales Invoice', icon: FileText },
  ],
};

function ProtectedRoute({ user, children }) {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is already logged in
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const res = await fetch('/api/auth/me', {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    } finally {
      setLoading(false);
    }
  }

  function handleLogin(userData) {
    setUser(userData);
    navigate('/', { replace: true });
  }

  async function handleLogout() {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  }

  // Inactivity auto-logout (5 minutes)
  const IDLE_TIMEOUT = 5 * 60 * 1000;       // 5 minutes
  const WARNING_TIME = 4 * 60 * 1000;       // Show warning at 4 minutes
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    setShowIdleWarning(false);
    setCountdown(60);
    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownRef.current);

    idleTimerRef.current = setTimeout(() => {
      setShowIdleWarning(true);
      let seconds = 60;
      setCountdown(seconds);
      countdownRef.current = setInterval(() => {
        seconds -= 1;
        setCountdown(seconds);
        if (seconds <= 0) {
          clearInterval(countdownRef.current);
          handleLogout();
        }
      }, 1000);
    }, WARNING_TIME);
  }, []);

  useEffect(() => {
    if (!user) return;
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(e => window.addEventListener(e, resetIdleTimer));
    resetIdleTimer();
    return () => {
      events.forEach(e => window.removeEventListener(e, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
      clearInterval(countdownRef.current);
    };
  }, [user, resetIdleTimer]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner-large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  const menuItems = roleMenus[user.role] || roleMenus.admin;

  return (
    <div className="app-container">
        {/* Mobile sidebar backdrop */}
        <div className={`sidebar-backdrop ${sidebarOpen ? 'show' : ''}`} onClick={() => setSidebarOpen(false)} />
        
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <img src="/logo.jpeg" alt="YSU Food Solutions" className="sidebar-logo" />
            <div className="logo-text">
              <h1>YFS Dispatch</h1>
              <span>Monitoring System</span>
            </div>
            <button className="close-btn" onClick={() => setSidebarOpen(false)}>
              <X size={20} />
            </button>
          </div>
          
          <div className="user-info">
            <div className="user-avatar">
              {user.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="user-details">
              <div className="user-name">{user.full_name}</div>
              <div className="user-role">{formatRole(user.role)}</div>
            </div>
          </div>

          <nav className="sidebar-nav">
            {menuItems.map(item => {
              const Icon = item.icon;
              return (
                <NavLink 
                  key={item.path}
                  to={item.path} 
                  end={item.path === '/'}
                  className={({isActive}) => isActive ? 'nav-link active' : 'nav-link'} 
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon size={20} /> 
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
          
          <div className="sidebar-footer">
            <button className="logout-button" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Logout</span>
            </button>
            <p className="company-name">YSU Marketing Corp.</p>
          </div>
        </aside>

        <main className="main-content">
          <header className="top-bar">
            <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <Menu size={24} />
            </button>
            <h2>YFS Dispatch Monitoring</h2>
            <div className="top-bar-right">
              <span className="date-display">{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </header>
          <div className="content-area">
            <Routes>
              <Route path="/" element={<ProtectedRoute user={user}><Dashboard /></ProtectedRoute>} />
              <Route path="/deliveries" element={<ProtectedRoute user={user}><Deliveries /></ProtectedRoute>} />
              <Route path="/customers" element={<ProtectedRoute user={user}><Customers /></ProtectedRoute>} />
              <Route path="/pull-outs" element={<ProtectedRoute user={user}><PullOuts /></ProtectedRoute>} />
              <Route path="/printing" element={<ProtectedRoute user={user}><Printing /></ProtectedRoute>} />
              <Route path="/posted-sales-invoice" element={<ProtectedRoute user={user}><PostedSalesInvoice /></ProtectedRoute>} />
              <Route path="/ar-received" element={<ProtectedRoute user={user}><ARReceived /></ProtectedRoute>} />
              <Route path="/deleted-deliveries" element={<ProtectedRoute user={user}><DeletedDeliveries /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute user={user}><Settings /></ProtectedRoute>} />
            </Routes>
          </div>
        </main>

        {/* Idle timeout warning modal */}
        {showIdleWarning && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white', borderRadius: 16, padding: 32, maxWidth: 400, width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center'
            }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>⏱️</div>
              <h3 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 700, color: '#1e293b' }}>Session Expiring</h3>
              <p style={{ margin: '0 0 20px', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
                You have been inactive for a while. You will be logged out automatically in{' '}
                <strong style={{ color: countdown <= 10 ? '#ef4444' : '#f59e0b' }}>{countdown}s</strong>.
              </p>
              <button
                onClick={resetIdleTimer}
                style={{
                  padding: '12px 32px', borderRadius: 8, border: 'none',
                  background: '#dc2626', color: 'white', fontWeight: 700, fontSize: 15,
                  cursor: 'pointer', fontFamily: 'inherit'
                }}
              >
                Stay Active
              </button>
            </div>
          </div>
        )}
      </div>
  );
}

export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
